/**
 * One tile in the Bexio overview's KPI row.
 *
 * Presentation only: it receives an already-formatted value and never derives
 * one. That keeps the arithmetic in the page, where the real response shapes
 * are, and stops a tile from quietly inventing a number.
 *
 * `value` is a node rather than a string so the connection tile can put a status
 * pill where the others put a figure, without a second component or a `variant`
 * flag that means "render something else entirely".
 */
import React from "react";

/**
 * Icon tints, written out per tone so Tailwind's JIT sees whole class strings.
 * Never build these by interpolation — the classes get purged.
 */
const TONE = {
  neutral: "bg-muted text-muted-foreground",
  primary: "bg-primary/10 text-primary",
  good: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400",
  warn: "bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300",
  bad: "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300",
};

export default function BexioKpiCard({ icon: Icon, tone = "primary", label, value, hint }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
      <span
        aria-hidden="true"
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
          TONE[tone] || TONE.primary
        }`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <div className="mt-1 text-xl font-semibold leading-tight text-foreground">
          {value}
        </div>
        {hint ? (
          <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>
        ) : null}
      </div>
    </div>
  );
}
